import axios from "../utils/axios";
import { PHONE_LOCALE } from "../utils/phoneLocale";

export interface IUser {
  id: string;
  firstName: string;
  middleName: string|null;
  lastName: string;
  role: UserRole;
  email: string|null;
  phone: string|null;
  avatar: string|null;
  tg: {
    firstName: null|string
    lastName: null|string
    username: null|string
  }|null;
}

export enum UserRole {
  EXECUTOR = 'executor',
  CUSTOMER = 'customer',
}

interface IAuth {
  accessToken: string;
  refreshToken: string;
}

export interface IValidation {
  [key: string]: string[];
}

// Способ входа/идентификации. telegram доступен только для входа.
export type AuthMethod = "phone" | "email" | "telegram";

// Для телефона бэкенд ожидает username с кодом страны без «+»:
// код зависит от домена кабинета (см. PHONE_LOCALE).
// Для email/telegram — значение передаётся как есть.
const buildIdentifier = (username: string, method: AuthMethod): string =>
  method === "phone" ? PHONE_LOCALE.code + username : username;

export const fetchSignedIn = async (): Promise<IUser> => {
  const {data} = await axios.get("/frontend-api/signed-in-user");

  return data.data;
};

export const signIn = async (username: string, password: string, method: AuthMethod = "phone"): Promise<IAuth> => {
  const {data} = await axios.post("/frontend-api/sign-in", { method, username: buildIdentifier(username, method), password });

  return data;
};

export const fetchSwitchToCabinet = async (id: string, date: string, signature: string): Promise<IAuth> => {
  const {data} = await axios.post("/frontend-api/switch-to-cabinet", { id, date, signature });

  return data;
};

// Способ подтверждения регистрации: код из СМС либо «Мобильный ID» оператора.
export type SignUpConfirmation = "code" | "mobileid";

export type MobileIdStatus = "pending" | "otp_required" | "confirmed" | "failed";

export interface IMobileIdStatus {
  status: MobileIdStatus;
  error?: string;
}

export interface IMobileIdOtpResult {
  httpStatus: number;
  status?: MobileIdStatus;
  error?: string;
  retryCount?: number;
}

// При подтверждении через «Мобильный ID» приватного кода нет — бэкенд сам знает,
// что сессия подтверждена оператором.
export const signUp = async (publicCode: string, privateCode?: string): Promise<IAuth> => {
  const payload = privateCode === undefined ? { publicCode } : { publicCode, privateCode };
  const {data} = await axios.post("/frontend-api/sign-up", payload);

  return data;
};

// Регистрация и восстановление поддерживают только phone и email.
export const createSignUpRequest = async (username: string, password: string, method: "phone" | "email" = "phone"): Promise<{ code: string, confirmation: SignUpConfirmation }> => {
  const {data} = await axios.post("/frontend-api/sign-up-request", { method, username: buildIdentifier(username, method), password });

  return data;
};

export const fetchMobileIdStatus = async (publicCode: string): Promise<IMobileIdStatus> => {
  const {data} = await axios.get(`/frontend-api/sign-up-request/${publicCode}/mobileid-status`);

  return data;
};

// Коды ответа здесь различают сценарии (422 — неверный код, 410 — сессия истекла
// и т.д.), поэтому статус не съедается интерцептором, а возвращается наверх.
export const sendMobileIdOtp = async (publicCode: string, code: string): Promise<IMobileIdOtpResult> => {
  const response = await axios.post(
      `/frontend-api/sign-up-request/${publicCode}/mobileid-otp`,
      { code },
      { validateStatus: () => true }
  );

  return {
    httpStatus: response.status,
    status: response.data?.status,
    error: response.data?.error,
    retryCount: response.data?.retryCount,
  };
};

export const createForgetPasswordRequest = async (username: string, method: "phone" | "email" = "phone"): Promise<{ code: string }> => {
  const {data} = await axios.post("/frontend-api/forget-password-request", { method, username: buildIdentifier(username, method) });

  return data;
};

export const checkForgetPasswordRequest = async (publicCode: string, privateCode: string): Promise<boolean> => {
  const {data} = await axios.post("/frontend-api/check-forget-password-code-request", { publicCode, privateCode });

  return data;
};

export const setNewPasswordAnonRequest = async (privateCode: string, publicCode: string, password: string, password_confirmation: string): Promise<IAuth> => {
  const {data} = await axios.post("/frontend-api/set-new-password", { publicCode, privateCode, password, password_confirmation });

  return data;
};

export const setNewPasswordRequest = async (currentPassword: string, newPassword: string): Promise<IAuth> => {
  const {data} = await axios.patch("/frontend-api/user/set-new-password", { newPassword, currentPassword, newPassword_confirmation: newPassword });

  return data;
};

export const verifyCaptcha = async (captcha: string): Promise<{ success: boolean }> => {
  const {data} = await axios.post("/frontend-api/verify-captcha", { captcha });

  return data;
};

export const updateContacts = async (email?: string|null, phone?: string|null): Promise<IAuth> => {
    const {data} = await axios.patch("/frontend-api/user/update-contacts", { email, phone });

    return data;
};

export const deleteTgIntegration = async (): Promise<IUser> => {
  const {data} = await axios.delete("/frontend-api/user/tg-integration");

  return data.data;
};

export const testTgIntegration = async (): Promise<void> => {
  const {data} = await axios.patch("/frontend-api/user/tg-integration");

  return data.data;
};

export const addTgIntegration = async (id: string, signature: string): Promise<IUser> => {
  const {data} = await axios.post("/frontend-api/user/tg-integration", { id, signature });

  return data.data;
};

export const getTgIntegrationLink = async (): Promise<string> => {
  const res = await axios.get("/frontend-api/user/tg-integration/link");

  return res.data;
};